import React, { useEffect, useMemo, useState } from 'react';
import { apiService } from '../../services/api';
import { useAuth } from '../../context/AuthContext';
import { Cliente } from '../../types';
import './ContatosRh.css';

interface RhFormState {
  nomeContato: string;
  telefone: string;
  nomeEmpresa: string;
  email: string;
  endereco: string;
}

const EMPTY_FORM: RhFormState = {
  nomeContato: '',
  telefone: '',
  nomeEmpresa: '',
  email: '',
  endereco: '',
};

function getNomeContato(cliente: Cliente): string {
  return cliente.dadosAdicionais?.nomeContato || cliente.nome;
}

function getNomeEmpresa(cliente: Cliente): string {
  return cliente.dadosAdicionais?.nomeEmpresa || '';
}

function getEndereco(cliente: Cliente): string {
  if (cliente.endereco?.logradouro) {
    const e = cliente.endereco;
    const parts = [e.logradouro, e.numero, e.complemento, e.bairro, e.cidade, e.estado, e.cep].filter(Boolean);
    return parts.join(', ');
  }
  return '';
}

function isRh(cliente: Cliente): boolean {
  return cliente.dadosAdicionais?.tipoContato === 'rh';
}

export const ContatosRh: React.FC = () => {
  const { usuario, isAdmin, isGerente, isOwner } = useAuth();
  const canManageAll = isOwner || isAdmin || isGerente;

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [clientes, setClientes] = useState<Cliente[]>([]);
  const [editing, setEditing] = useState<Cliente | null>(null);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState<RhFormState>(EMPTY_FORM);

  const contatosRh = useMemo(
    () => clientes.filter(isRh),
    [clientes]
  );

  const loadContatos = async () => {
    try {
      setLoading(true);
      setError(null);
      const allClientes = await apiService.getClientes();
      setClientes(allClientes);
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao carregar contatos de parceiro');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadContatos();
  }, []);

  const canEditCliente = (cliente: Cliente) => {
    if (canManageAll) return true;
    return cliente.dadosAdicionais?.criadoPor === usuario?.usuarioId;
  };

  const openNewForm = () => {
    setEditing(null);
    setForm(EMPTY_FORM);
    setShowForm(true);
  };

  const openEditForm = (cliente: Cliente) => {
    setEditing(cliente);
    setForm({
      nomeContato: getNomeContato(cliente),
      telefone: cliente.telefone || '',
      nomeEmpresa: getNomeEmpresa(cliente),
      email: cliente.email || '',
      endereco: getEndereco(cliente),
    });
    setShowForm(true);
  };

  const closeForm = () => {
    if (saving) return;
    setShowForm(false);
    setEditing(null);
    setForm(EMPTY_FORM);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!form.nomeContato.trim() || !form.telefone.trim() || !form.nomeEmpresa.trim()) {
      setError('Nome do contato, WhatsApp e nome da empresa são obrigatórios');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const payload: Partial<Cliente> = {
        nome: form.nomeContato.trim(),
        telefone: form.telefone.trim(),
        email: form.email.trim() || undefined,
        endereco: form.endereco.trim() ? { logradouro: form.endereco.trim() } : undefined,
        dadosAdicionais: {
          ...(editing?.dadosAdicionais || {}),
          tipoContato: 'rh',
          nomeContato: form.nomeContato.trim(),
          nomeEmpresa: form.nomeEmpresa.trim(),
        },
      };

      if (editing) {
        await apiService.updateCliente(editing.clienteId, payload);
      } else {
        await apiService.createCliente(payload);
      }

      await loadContatos();
      closeForm();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao salvar contato de parceiro');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (cliente: Cliente) => {
    const confirmed = window.confirm(`Excluir contato de parceiro "${getNomeContato(cliente)}"?`);
    if (!confirmed) return;

    try {
      setError(null);
      await apiService.deleteCliente(cliente.clienteId);
      await loadContatos();
    } catch (err: any) {
      setError(err?.response?.data?.message || 'Erro ao excluir contato de parceiro');
    }
  };

  return (
    <div className="contatos-rh-page">
      <div className="contatos-rh-wrap">
        <header className="contatos-rh-header">
          <div>
            <h1>Contatos de Parceiro</h1>
            <p>Gerencie os contatos usados para identificar parceiros automaticamente.</p>
          </div>
          <div className="contatos-rh-actions">
            <button className="btn btn-primary" onClick={openNewForm}>
              + Novo contato de parceiro
            </button>
          </div>
        </header>

        {error && <div className="contatos-rh-error">{error}</div>}

        {loading ? (
          <div className="contatos-rh-loading">Carregando contatos...</div>
        ) : (
          <div className="contatos-rh-table">
            <div className="contatos-rh-row contatos-rh-row-head">
              <span>Contato RH</span>
              <span>WhatsApp</span>
              <span>Empresa</span>
              <span>E-mail</span>
              <span>Endereco</span>
              <span>Criado por</span>
              <span>Acoes</span>
            </div>
            {contatosRh.length === 0 && (
              <div className="contatos-rh-empty">Nenhum contato de parceiro cadastrado.</div>
            )}
            {contatosRh.map((cliente) => {
              const editable = canEditCliente(cliente);
              return (
                <div className="contatos-rh-row" key={cliente.clienteId}>
                  <span>{getNomeContato(cliente)}</span>
                  <span>{cliente.telefone}</span>
                  <span>{getNomeEmpresa(cliente) || '-'}</span>
                  <span>{cliente.email || '-'}</span>
                  <span>{getEndereco(cliente) || '-'}</span>
                  <span>{cliente.dadosAdicionais?.criadoPorNome || '-'}</span>
                  <span className="contatos-rh-row-actions">
                    {editable && (
                      <>
                        <button className="btn-link" onClick={() => openEditForm(cliente)}>
                          Editar
                        </button>
                        <button className="btn-link btn-danger" onClick={() => handleDelete(cliente)}>
                          Excluir
                        </button>
                      </>
                    )}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {showForm && (
        <div className="rh-modal-overlay" onClick={closeForm}>
          <div className="rh-modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editing ? 'Editar Contato de Parceiro' : 'Novo Contato de Parceiro'}</h2>
            <form onSubmit={handleSave}>
              <label>Nome do contato *</label>
              <input
                type="text"
                value={form.nomeContato}
                onChange={(e) => setForm({ ...form, nomeContato: e.target.value })}
                disabled={saving}
                required
              />

              <label>WhatsApp/Telefone *</label>
              <input
                type="tel"
                value={form.telefone}
                onChange={(e) => setForm({ ...form, telefone: e.target.value })}
                disabled={saving}
                required
              />

              <label>Nome da empresa *</label>
              <input
                type="text"
                value={form.nomeEmpresa}
                onChange={(e) => setForm({ ...form, nomeEmpresa: e.target.value })}
                disabled={saving}
                required
              />

              <label>E-mail</label>
              <input
                type="email"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                disabled={saving}
              />

              <label>Endereco</label>
              <input
                type="text"
                value={form.endereco}
                onChange={(e) => setForm({ ...form, endereco: e.target.value })}
                disabled={saving}
                placeholder="Rua, numero, cidade, estado"
              />

              <div className="rh-modal-actions">
                <button type="button" className="btn btn-ghost" onClick={closeForm} disabled={saving}>
                  Cancelar
                </button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Salvando...' : 'Salvar'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};
